import { colors, motion, radii, strokes, tint, toneColor } from '@kk/ui';

import { portalColors, portalLayout, portalText } from './portalTokens';

/**
 * **The web portal's muted and link text, darkened to pass WCAG AA** (the 23 Oct audit, T2-11 and Tier 4 #6). @kk/ui's
 * Text reads its tone colours from `toneColor` at render, so setting the two here, once, before the first render, moves
 * every muted and link line in the portal without touching a screen. This runs only in the portal's own bundle
 * (main.tsx); the app's tokens are not changed.
 */
export function applyPortalTextTones(tones: Record<'muted' | 'link', string> = toneColor): void {
  tones.muted = portalText.muted;
  tones.link = portalText.link;
}

/**
 * The stylesheet (styles.css) reads every colour and metric from CSS custom properties set here, so no
 * hex literal lives outside @kk/ui tokens and portalTokens.ts. Called once before the first render.
 */
export function installCssVars(root: HTMLElement = document.documentElement): void {
  const set = (name: string, value: string | number) =>
    root.style.setProperty(`--${name}`, typeof value === 'number' ? `${value}px` : value);
  for (const [k, v] of Object.entries(colors)) set(`kk-${k}`, v);
  // the tints too (O13's diff marks words with `wasabiPale` / `orangePale`), so no hex lives in the stylesheet
  for (const [k, v] of Object.entries(tint)) set(`kk-tint-${k}`, v);
  for (const [k, v] of Object.entries(portalColors)) set(`portal-${k}`, v);
  // the stylesheet's muted and teal text, the same two colours as the Text tones above
  set('kk-muted', portalText.muted);
  set('kk-deepTeal', portalText.link);
  applyPortalTextTones();
  set('portal-sidebar', portalLayout.sidebar);
  set('portal-main-pad-y', portalLayout.mainPadY);
  set('portal-main-pad-x', portalLayout.mainPadX);
  set('portal-table-min', portalLayout.tableMin);
  set('portal-phone-gutter', portalLayout.phoneGutter);
  set('portal-sticky-bottom', portalLayout.stickyBottom);
  set('portal-cell-pad-y', portalLayout.cellPadY);
  set('portal-cell-pad-x', portalLayout.cellPadX);
  set('portal-grid-gap', portalLayout.gridGap);
  set('portal-approvals-grid', portalLayout.approvalsGrid);
  set('portal-roster-grid', portalLayout.rosterGrid);
  set('portal-assignments-grid', portalLayout.assignmentsGrid);
  set('portal-staff-grid', portalLayout.staffGrid);
  set('portal-logo-radius', portalLayout.logoRadius);
  set('portal-signin-radius', portalLayout.signInCardRadius);
  set('portal-signin-panel', portalLayout.signInPanelWidth);
  set('kk-radius-input', radii.input);
  set('kk-radius-row', radii.row);
  set('kk-radius-banner', radii.banner);
  set('kk-radius-button', radii.button);
  set('kk-radius-card', radii.card);
  set('kk-radius-sheet', radii.sheet);
  set('kk-radius-pill', radii.pill);
  set('kk-stroke-quiet', strokes.quiet);
  set('kk-stroke-playful', strokes.playful);
  set('kk-stroke-playful-bold', strokes.playfulBold);
  set('kk-ease', `cubic-bezier(${motion.ease.join(',')})`);
  set('kk-motion-fast', `${motion.fast}ms`);
  set('kk-motion-base', `${motion.base}ms`);
  set('kk-motion-slow', `${motion.slow}ms`);
}
